import React, { useState, useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { GISMap } from "@/components/GISMap";
import { Search, Filter, MapPin, Landmark } from "lucide-react";

const PROVINCES = ["Harare","Bulawayo","Manicaland","Mashonaland Central","Mashonaland East",
  "Mashonaland West","Masvingo","Matabeleland North","Matabeleland South","Midlands"];

const LAND_TYPES = ["freehold","leasehold","communal","state","agricultural","urban_residential",
  "urban_commercial","industrial","institutional","conservation","resettlement"];

export const LandCadastre = () => {
  const [q, setQ] = useState("");
  const [province, setProvince] = useState("");
  const [landType, setLandType] = useState("");
  const [selected, setSelected] = useState(null);

  const { data, isLoading } = useQuery({
    queryKey: ["parcels", q, province, landType],
    queryFn: async () => (await api.get("/land/parcels", {
      params: { q: q || undefined, province: province || undefined, land_type: landType || undefined, limit: 100 },
    })).data,
  });

  const items = data?.items || [];
  const total = data?.total || 0;

  return (
    <div className="space-y-6" data-testid="page-land">
      <header className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <div className="font-mono-cad text-[10px] uppercase tracking-[0.28em] text-[#7C93A5]">LandCadastre360™</div>
          <h1 className="font-display text-3xl font-bold text-[#F6F8FA] mt-1">Parcel Register</h1>
          <div className="text-[13px] text-[#B3C2CC] mt-1">{total.toLocaleString()} parcels in demonstration dataset · full lineage preserved.</div>
        </div>
      </header>

      <div className="grid grid-cols-1 lg:grid-cols-4 gap-4">
        <div className="lg:col-span-1 space-y-3">
          <div className="cad-panel p-4">
            <div className="font-mono-cad text-[10px] uppercase tracking-[0.22em] text-[#7C93A5] mb-2 flex items-center gap-2">
              <Filter size={12} /> Filters
            </div>
            <div className="space-y-3">
              <div>
                <label className="text-[11px] text-[#B3C2CC] block mb-1">Search</label>
                <div className="relative">
                  <Search size={13} className="absolute left-2.5 top-2.5 text-[#7C93A5]" />
                  <input
                    data-testid="land-search"
                    value={q} onChange={(e) => setQ(e.target.value)}
                    placeholder="Parcel #, district, holder"
                    className="w-full bg-[#071B2B] border cad-hairline rounded-sm pl-8 pr-2 py-2 text-[13px] text-[#F6F8FA] focus:ring-2 focus:ring-[#C99A2E] outline-none"
                  />
                </div>
              </div>
              <div>
                <label className="text-[11px] text-[#B3C2CC] block mb-1">Province</label>
                <select value={province} onChange={(e) => setProvince(e.target.value)}
                        data-testid="land-province"
                        className="w-full bg-[#071B2B] border cad-hairline rounded-sm px-2 py-2 text-[13px] text-[#F6F8FA]">
                  <option value="">All provinces</option>
                  {PROVINCES.map((p) => <option key={p} value={p}>{p}</option>)}
                </select>
              </div>
              <div>
                <label className="text-[11px] text-[#B3C2CC] block mb-1">Land type</label>
                <select value={landType} onChange={(e) => setLandType(e.target.value)}
                        data-testid="land-type"
                        className="w-full bg-[#071B2B] border cad-hairline rounded-sm px-2 py-2 text-[13px] text-[#F6F8FA]">
                  <option value="">All types</option>
                  {LAND_TYPES.map((t) => <option key={t} value={t}>{t.replace(/_/g, " ")}</option>)}
                </select>
              </div>
            </div>
          </div>

          {selected && (
            <div className="cad-panel p-4" data-testid="parcel-detail">
              <div className="font-mono-cad text-[10px] uppercase tracking-[0.22em] text-[#C99A2E]">Selected Parcel</div>
              <div className="font-display text-lg font-bold text-[#F6F8FA] mt-1">{selected.parcel_number}</div>
              <div className="mt-2 text-[13px] text-[#DCE3E8]">
                <div>{selected.district}, {selected.province}</div>
                <div className="uppercase text-[10px] tracking-widest font-mono-cad text-[#7C93A5] mt-1">
                  {selected.land_type} · {selected.tenure}
                </div>
              </div>
              <dl className="mt-3 text-[12px] space-y-1.5 text-[#B3C2CC]">
                <div className="flex justify-between"><dt>Area</dt><dd className="text-[#F6F8FA] font-mono-cad">{selected.area_ha} ha</dd></div>
                <div className="flex justify-between"><dt>Rights Holder</dt><dd className="text-[#F6F8FA] text-right max-w-[60%]">{selected.rights_holder || "Unallocated"}</dd></div>
                <div className="flex justify-between"><dt>Zoning</dt><dd className="text-[#F6F8FA]">{selected.zoning}</dd></div>
                <div className="flex justify-between"><dt>Use</dt><dd className="text-[#F6F8FA]">{selected.land_use}</dd></div>
                <div className="flex justify-between"><dt>Title</dt><dd>{selected.title_registered ? <span className="cad-chip cad-chip-green">Registered</span> : <span className="cad-chip cad-chip-red">Untitled</span>}</dd></div>
              </dl>
              <a href={`/verify/${selected.parcel_number}`} target="_blank" rel="noopener noreferrer"
                 className="cad-btn-outline block text-center mt-4 py-2 text-[12px] rounded-sm" data-testid="parcel-verify">
                Verify certificate
              </a>
            </div>
          )}
        </div>

        <div className="lg:col-span-3 space-y-4">
          <GISMap parcels={items} height={420} onFeatureClick={(_, f) => setSelected(f)} />
          <div className="cad-panel overflow-hidden">
            <table className="w-full text-[13px]">
              <thead>
                <tr className="text-left border-b cad-hairline">
                  {["Parcel #", "Province", "District", "Type", "Tenure", "Area (ha)", "Rights Holder", "Status"].map((h) => (
                    <th key={h} className="px-3 py-2.5 font-mono-cad text-[10px] uppercase tracking-[0.18em] text-[#7C93A5]">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody className="cad-tbody">
                {isLoading && <tr><td colSpan={8} className="px-3 py-6 text-center text-[#7C93A5]">Loading…</td></tr>}
                {items.map((p) => (
                  <tr key={p.id} onClick={() => setSelected(p)} className="cursor-pointer" data-testid={`parcel-row-${p.id}`}>
                    <td className="px-3 py-2 font-mono-cad text-[#F6F8FA]">{p.parcel_number}</td>
                    <td className="px-3 py-2">{p.province}</td>
                    <td className="px-3 py-2">{p.district}</td>
                    <td className="px-3 py-2 capitalize">{(p.land_type || "").replace(/_/g, " ")}</td>
                    <td className="px-3 py-2 text-[#B3C2CC]">{p.tenure?.replace(/_/g, " ")}</td>
                    <td className="px-3 py-2 font-mono-cad">{p.area_ha}</td>
                    <td className="px-3 py-2 text-[#DCE3E8]">{p.rights_holder || <span className="text-[#7C93A5] italic">Unallocated</span>}</td>
                    <td className="px-3 py-2">
                      {p.title_registered
                        ? <span className="cad-chip cad-chip-green">Titled</span>
                        : <span className="cad-chip cad-chip-red">Untitled</span>}
                    </td>
                  </tr>
                ))}
                {!isLoading && items.length === 0 && (
                  <tr><td colSpan={8} className="px-3 py-6 text-center text-[#7C93A5]">No parcels match your filters.</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
};
